import { Link } from 'react-router-dom';
import { formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Claim, EffectiveRate, MemberStatement, VoluntaryStatement } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

export function MemberOverview() {
  const { persona } = useAuth();
  const statementQ = useApiQuery<MemberStatement>('/api/v1/members/me/statement');
  const claimsQ = useApiQuery<{ items: Claim[] }>('/api/v1/benefits/me/claims');
  const voluntaryQ = useApiQuery<VoluntaryStatement>('/api/v1/voluntary/me/statement');
  const rateQ = useApiQuery<EffectiveRate>('/api/v1/actuarial/rates/effective');

  const memberNotFound = statementQ.error?.toLowerCase().includes('not found') ?? false;
  const voluntaryNotFound = voluntaryQ.error?.toLowerCase().includes('not found') ?? false;
  const latestClaim = claimsQ.data?.items[0];
  const firstName = persona?.name.split(' ')[0] ?? 'Member';

  return (
    <div>
      <PageHeader title={`Welcome, ${firstName}`} subtitle="Your Seychelles Pension Fund summary" />

      {statementQ.loading ? (
        <Spinner />
      ) : memberNotFound ? (
        <div className="mb-6">
          <EmptyState
            title="No pension record yet"
            hint="Your SPF account is created automatically the first time your employer posts a contribution for you."
          />
        </div>
      ) : statementQ.error ? (
        <div className="mb-6">
          <ErrorNote message={statementQ.error} />
        </div>
      ) : statementQ.data ? (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total contributions to date" value={scr(statementQ.data.totals.totalContrib)} hint="Member registry (P01)" />
          <StatCard label="Years of contribution" value={statementQ.data.totals.yearsOfContribution} />
          <StatCard
            label="Voluntary savings balance"
            value={voluntaryNotFound ? scr(0) : voluntaryQ.loading ? '…' : scr(voluntaryQ.data?.balance ?? 0)}
            hint="Voluntary top-up savings (P04)"
          />
          <StatCard label="Membership status" value={<StatusBadge status={statementQ.data.member.status} />} />
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-4">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Recent contribution years</h2>
          {statementQ.loading ? (
            <Spinner />
          ) : (statementQ.data?.years.length ?? 0) === 0 ? (
            <EmptyState title="No contribution years posted yet" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Year</th>
                  <th className="table-head text-right">Months</th>
                  <th className="table-head text-right">Wages</th>
                  <th className="table-head text-right">Contributed</th>
                </tr>
              </thead>
              <tbody>
                {[...statementQ.data!.years]
                  .reverse()
                  .slice(0, 5)
                  .map((y) => (
                    <tr key={y.year}>
                      <td className="table-cell font-medium">{y.year}</td>
                      <td className="table-cell text-right">{y.months}</td>
                      <td className="table-cell text-right tabular-nums">{scr(y.totalWages)}</td>
                      <td className="table-cell text-right tabular-nums">{scr(y.totalContrib)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
          <Link to="/member/contributions" className="mt-3 inline-block text-xs font-semibold text-brand-600 hover:underline">
            View full contribution history &amp; pension projection →
          </Link>
        </div>

        <div className="card p-4">
          <h2 className="mb-3 text-sm font-bold text-slate-700">Latest benefit claim</h2>
          {claimsQ.loading ? (
            <Spinner />
          ) : !latestClaim ? (
            <EmptyState title="No benefit claims" hint="Submit a claim when you're ready to retire or need incapacity/survivor benefits." />
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <span className="font-mono text-xs text-slate-500">{latestClaim.refNumber}</span>
                <StatusBadge status={latestClaim.status} />
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-800">{latestClaim.type.replaceAll('_', ' ')}</div>
              {latestClaim.account ? (
                <div className="mt-2 text-lg font-bold tabular-nums text-seagreen-600">
                  {scr(latestClaim.account.monthlyAmount)} / month
                </div>
              ) : null}
            </div>
          )}
          <Link to="/member/benefits" className="mt-3 inline-block text-xs font-semibold text-brand-600 hover:underline">
            View all claims &amp; submit a new one →
          </Link>
        </div>
      </div>

      {rateQ.data ? (
        <p className="mt-6 text-xs text-slate-400">
          Current legislated contribution rate: {rateQ.data.employeePct}% employee + {rateQ.data.employerPct}% employer
          (effective {formatDate(rateQ.data.effectiveFrom)}, {rateQ.data.legalRef}).
        </p>
      ) : null}
    </div>
  );
}
